/* Règles de champs obligatoires par étape, contrôles de format et utilitaires de dates. */
import type { CrmContactDraft, EntityType, Role, Stage } from './types';
import { STAGE_INDEX, STAGE_ORDER, formatBce } from './types';
import { ibanError, isValidBic, normalizeBic } from '../../../supabase/functions/_shared/sepa';

/** Clé d'un champ : nom de colonne, ou « meters », « meter_names », « meters.N.ean_code », « meters.N.meter_address », « meters.N.name ». */
export type FieldKey = string;

export interface MissingField {
  key: FieldKey;
  label: string;
}

const BASE_LABELS: Record<string, string> = {
  role: 'Type (consommateur, producteur, prosumer)',
  entity_type: 'Personne physique / morale',
  first_name: 'Prénom',
  last_name: 'Nom',
  birth_date: 'Date de naissance',
  national_register: 'N° de registre national',
  company_name: 'Dénomination',
  legal_form: 'Forme juridique',
  company_number: 'N° BCE',
  representative: 'Représentant(e)',  // rempli à partir du prénom et du nom (société)
  representative_title: 'Qualité du représentant(e)',
  email: 'E-mail',
  phone: 'Téléphone',
  billing_email: 'E-mail de facturation',
  billing_address: 'Adresse de facturation',
  meters: 'Au moins un point de comptage',
  vat_regime: 'Régime TVA',
  iban: 'IBAN',
  account_holder: 'Titulaire du compte',
  bic: 'BIC',
  sepa_consent: 'Accord pour la domiciliation SEPA',
  installed_power_kwc: 'Puissance crête (kWc)',
  installation_right: 'Droit sur l’installation',
  price_cents_kwh: 'Prix (c€/kWh HTVA)',
  sell_price_cents_kwh: 'Prix de vente (c€/kWh HTVA)',
  annual_fee: 'Cotisation annuelle (€/an HTVA)',
  housing_type: 'Logement',
  annual_consumption_kwh: 'Consommation annuelle (kWh)',
  annual_cost_eur: 'Montant payé par an (€ TVAC)',
  contract_signed_at: 'Date de signature',
  sibelga_encoded_at: "Date d'encodage Sibelga",
  sharing_operation_id: 'Opération de partage',
  sharing_start_date: 'Date de début de partage',
  sharing_price_cents_kwh: 'Prix appliqué (c€/kWh HTVA)',
  sharing_sell_price_cents_kwh: 'Prix de vente appliqué (c€/kWh HTVA)',
};

export function fieldLabel(key: FieldKey, entity: EntityType | null | undefined, role?: Role | null): string {
  const meterMatch = /^meters\.(\d+)\.(ean_code|meter_address|name)$/.exec(key);
  if (meterMatch) {
    const n = Number(meterMatch[1]) + 1;
    if (meterMatch[2] === 'name') return `Nom du participant (compteur ${n})`;
    return meterMatch[2] === 'ean_code' ? `Code EAN du compteur ${n}` : `Adresse du compteur ${n}`;
  }
  if (key === 'meter_names') return 'Nom du participant de chaque compteur';
  if (key === 'domicile_address') return entity === 'morale' ? 'Siège social' : 'Domicile';
  if (key === 'first_name' && entity === 'morale') return 'Prénom du représentant(e)';
  if (key === 'last_name' && entity === 'morale') return 'Nom du représentant(e)';
  if (key === 'price_cents_kwh' && role === 'prosumer') return "Prix d'achat (c€/kWh HTVA)";
  if (key === 'sharing_price_cents_kwh' && role === 'prosumer') return "Prix d'achat appliqué (c€/kWh HTVA)";
  return BASE_LABELS[key] ?? key;
}

/** Champs saisis dans la fenêtre de changement d'étape (et non dans la fiche). */
export const DIALOG_FIELDS: FieldKey[] = [
  'contract_signed_at',
  'sibelga_encoded_at',
  'sharing_operation_id',
  'sharing_start_date',
  'sharing_price_cents_kwh',
  'sharing_sell_price_cents_kwh',
];

/* ── Champs obligatoires par étape (paramétrables dans CRM → Paramètres) ── */

/** Étape à partir de laquelle un champ est exigé, ou « never » (facultatif). */
export type RequirementStage = Stage | 'never';
export type RequirementOverrides = Record<string, RequirementStage>;

type Applies = (c: CrmContactDraft) => boolean;
const always: Applies = () => true;
const isPM: Applies = c => c.entity_type === 'morale';
const isPP: Applies = c => c.entity_type !== 'morale';
const producerLike: Applies = c => c.role === 'producer' || c.role === 'prosumer';
const isProsumer: Applies = c => c.role === 'prosumer';
/** Particulier consommateur qui a accepté la domiciliation : compte à prélever exigé. */
const sepaConsumer: Applies = c => c.role === 'consumer' && c.entity_type === 'physique' && !!c.sepa_consent;

export interface RequirementRule {
  /** Identifiant de la règle (stocké dans les paramètres). */
  id: string;
  label: string;
  section: string;
  /** À qui la règle s'applique, en clair. */
  appliesLabel?: string;
  applies: Applies;
  /** Clé du champ contrôlé (peut dépendre du contact, ex. nom ou dénomination). */
  field: (c: CrmContactDraft) => FieldKey;
  defaultStage: RequirementStage;
  /** Étapes autorisées dans les paramètres ; une seule = règle verrouillée. */
  allowed: RequirementStage[];
  /** Pourquoi la règle est verrouillée ou limitée. */
  lockReason?: string;
}

const ALL: RequirementStage[] = [...STAGE_ORDER, 'never'];
const UP_TO_ACTIVE: RequirementStage[] = [...STAGE_ORDER];
const ADMIN_ESSENTIAL = 'Champ obligatoire du formulaire participant de la page admin : exigé au plus tard pour « Membre actif ».';
const fixed = (stage: Stage, reason: string) => ({ defaultStage: stage, allowed: [stage] as RequirementStage[], lockReason: reason });
const k = (key: FieldKey) => () => key;

export const REQUIREMENT_RULES: RequirementRule[] = [
  { id: 'role', label: 'Type (consommateur, producteur, prosumer)', section: 'Profil', applies: always, field: k('role'),
    ...fixed('lead', 'Détermine le modèle de contrat : exigé dès le lead.') },
  { id: 'entity_type', label: 'Personne physique / morale', section: 'Profil', applies: always, field: k('entity_type'),
    ...fixed('lead', 'Détermine les champs d’identité : exigé dès le lead.') },
  { id: 'name', label: 'Nom (personne) ou dénomination (société)', section: 'Profil', applies: always,
    field: c => (c.entity_type === 'morale' ? 'company_name' : 'last_name'), ...fixed('lead', 'Sert à afficher le contact : exigé dès le lead.') },
  { id: 'email', label: 'E-mail', section: 'Coordonnées', applies: always, field: k('email'), defaultStage: 'lead', allowed: ALL },
  // Facultatif dans le formulaire d'adhésion : jamais exigé par défaut.
  { id: 'phone', label: 'Téléphone', section: 'Coordonnées', applies: always, field: k('phone'), defaultStage: 'never', allowed: ALL },
  { id: 'domicile_address', label: 'Domicile / siège social', section: 'Coordonnées', applies: always, field: k('domicile_address'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'billing_email', label: 'E-mail de facturation', section: 'Coordonnées', applies: always, field: k('billing_email'), defaultStage: 'never', allowed: ALL },
  { id: 'billing_address', label: 'Adresse de facturation', section: 'Coordonnées', applies: always, field: k('billing_address'), defaultStage: 'never', allowed: ALL },
  { id: 'first_name', label: 'Prénom', section: 'Identité', appliesLabel: 'Personne physique', applies: isPP, field: k('first_name'), defaultStage: 'contract_sent', allowed: ALL },
  // Absent des conventions Sun Is Up : facultatif par défaut.
  { id: 'national_register', label: 'N° de registre national', section: 'Identité', appliesLabel: 'Personne physique, consommateur ou prosumer',
    applies: c => isPP(c) && (c.role === 'consumer' || c.role === 'prosumer'), field: k('national_register'), defaultStage: 'never', allowed: ALL },
  { id: 'birth_date', label: 'Date de naissance', section: 'Identité', appliesLabel: 'Personne physique',
    applies: isPP, field: k('birth_date'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'legal_form', label: 'Forme juridique', section: 'Identité', appliesLabel: 'Société', applies: isPM, field: k('legal_form'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'company_number', label: 'N° BCE', section: 'Identité', appliesLabel: 'Société', applies: isPM, field: k('company_number'), defaultStage: 'contract_sent', allowed: ALL },
  // Société : nom du représentant(e), saisi comme pour un particulier (prénom et nom séparés).
  // Facultatif par défaut (modifiable dans CRM → Paramètres → Champs obligatoires).
  { id: 'representative', label: 'Nom du représentant(e)', section: 'Identité', appliesLabel: 'Société', applies: isPM, field: k('last_name'), defaultStage: 'never', allowed: ALL },
  { id: 'representative_title', label: 'Qualité du représentant(e)', section: 'Identité', appliesLabel: 'Société',
    applies: isPM, field: k('representative_title'), defaultStage: 'never', allowed: ALL },
  { id: 'meters', label: 'Code EAN et adresse de chaque compteur', section: 'Compteurs', applies: always, field: k('meters'),
    defaultStage: 'contract_sent', allowed: UP_TO_ACTIVE, lockReason: ADMIN_ESSENTIAL },
  { id: 'meter_names', label: 'Nom du participant de chaque compteur', section: 'Compteurs', applies: always, field: k('meter_names'),
    defaultStage: 'active', allowed: UP_TO_ACTIVE, lockReason: ADMIN_ESSENTIAL },
  // La convention producteur PP ne prévoit que « non assujetti » : régime demandé aux sociétés uniquement.
  { id: 'vat_regime', label: 'Régime TVA', section: 'Producteur', appliesLabel: 'Société, producteur ou prosumer',
    applies: c => isPM(c) && producerLike(c), field: k('vat_regime'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'installed_power_kwc', label: 'Puissance crête de l’installation (kWc)', section: 'Producteur', appliesLabel: 'Producteur ou prosumer',
    applies: producerLike, field: k('installed_power_kwc'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'installation_right', label: 'Droit sur l’installation', section: 'Producteur', appliesLabel: 'Producteur ou prosumer',
    applies: producerLike, field: k('installation_right'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'iban', label: 'IBAN', section: 'Compte bancaire', appliesLabel: 'Producteur ou prosumer ; particulier consommateur en domiciliation',
    applies: c => producerLike(c) || sepaConsumer(c), field: k('iban'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'account_holder', label: 'Titulaire du compte', section: 'Compte bancaire', appliesLabel: 'Producteur ou prosumer ; particulier consommateur en domiciliation',
    applies: c => producerLike(c) || sepaConsumer(c), field: k('account_holder'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'bic', label: 'BIC', section: 'Compte bancaire', appliesLabel: 'Particulier consommateur en domiciliation',
    applies: sepaConsumer, field: k('bic'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'price_cents_kwh', label: 'Prix du contrat', section: 'Contrat', applies: always, field: k('price_cents_kwh'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'sell_price_cents_kwh', label: 'Prix de vente du contrat', section: 'Contrat', appliesLabel: 'Prosumer', applies: isProsumer, field: k('sell_price_cents_kwh'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'annual_fee', label: 'Cotisation annuelle', section: 'Contrat', applies: always, field: k('annual_fee'), defaultStage: 'contract_sent', allowed: ALL },
  { id: 'contract_signed_at', label: 'Date de signature', section: 'Étapes', applies: always, field: k('contract_signed_at'),
    ...fixed('contract_signed', 'Demandée dans la fenêtre de passage à l’étape.') },
  { id: 'sibelga_encoded_at', label: 'Date d’encodage Sibelga', section: 'Étapes', applies: always, field: k('sibelga_encoded_at'),
    ...fixed('sibelga', 'Posée automatiquement au passage à l’étape (date du jour), modifiable dans la fiche.') },
  { id: 'sharing_operation_id', label: 'Opération de partage', section: 'Étapes', applies: always, field: k('sharing_operation_id'),
    ...fixed('sibelga', 'Demandée dans la fenêtre de passage à l’étape.') },
  { id: 'sharing_start_date', label: 'Date de début de partage', section: 'Étapes', applies: always, field: k('sharing_start_date'),
    ...fixed('active', `Demandée dans la fenêtre de passage à l’étape. ${ADMIN_ESSENTIAL}`) },
  { id: 'sharing_price_cents_kwh', label: 'Prix appliqué', section: 'Étapes', applies: always, field: k('sharing_price_cents_kwh'),
    ...fixed('active', `Demandé dans la fenêtre de passage à l’étape. ${ADMIN_ESSENTIAL}`) },
  { id: 'sharing_sell_price_cents_kwh', label: 'Prix de vente appliqué', section: 'Étapes', appliesLabel: 'Prosumer', applies: isProsumer,
    field: k('sharing_sell_price_cents_kwh'), ...fixed('active', `Demandé dans la fenêtre de passage à l’étape. ${ADMIN_ESSENTIAL}`) },
];

let requirementOverrides: RequirementOverrides = {};

/** Applique les réglages enregistrés (les valeurs non autorisées sont ignorées). */
export function setRequirementOverrides(overrides: RequirementOverrides | null | undefined): void {
  const clean: RequirementOverrides = {};
  for (const rule of REQUIREMENT_RULES) {
    const v = overrides?.[rule.id];
    if (v && rule.allowed.includes(v)) clean[rule.id] = v;
  }
  requirementOverrides = clean;
}

export function getRequirementOverrides(): RequirementOverrides {
  return { ...requirementOverrides };
}

export function requirementStage(rule: RequirementRule, overrides: RequirementOverrides = requirementOverrides): RequirementStage {
  const v = overrides[rule.id];
  return v && rule.allowed.includes(v) ? v : rule.defaultStage;
}

/** Champs exigés pour ENTRER dans une étape (hors étapes précédentes). */
function stageRequirements(stage: Stage, c: CrmContactDraft): FieldKey[] {
  return REQUIREMENT_RULES.filter(r => requirementStage(r) === stage && r.applies(c)).map(r => r.field(c));
}

/** Champs exigés pour atteindre `target` : cumul des étapes 0 à target. */
export function requiredFieldsFor(c: CrmContactDraft, target: Stage): FieldKey[] {
  const keys = new Set<FieldKey>();
  for (const s of STAGE_ORDER.slice(0, STAGE_INDEX[target] + 1)) {
    stageRequirements(s, c).forEach(k => keys.add(k));
  }
  return Array.from(keys);
}

export function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/**
 * Champs manquants pour atteindre `target`.
 * excludeDialogFields : ignore les champs saisis dans la fenêtre de changement d'étape.
 */
export function missingFields(
  c: CrmContactDraft,
  target: Stage,
  options: { excludeDialogFields?: boolean } = {},
): MissingField[] {
  const out: MissingField[] = [];
  for (const key of requiredFieldsFor(c, target)) {
    if (options.excludeDialogFields && DIALOG_FIELDS.includes(key)) continue;
    if (key === 'meters') {
      const meters = c.meters ?? [];
      if (meters.length === 0) {
        out.push({ key, label: fieldLabel(key, c.entity_type, c.role) });
        continue;
      }
      meters.forEach((m, i) => {
        if (isEmpty(m.ean_code)) out.push({ key: `meters.${i}.ean_code`, label: fieldLabel(`meters.${i}.ean_code`, c.entity_type) });
        if (isEmpty(m.meter_address)) out.push({ key: `meters.${i}.meter_address`, label: fieldLabel(`meters.${i}.meter_address`, c.entity_type) });
      });
      continue;
    }
    if (key === 'meter_names') {
      (c.meters ?? []).forEach((m, i) => {
        if (isEmpty(m.name)) out.push({ key: `meters.${i}.name`, label: fieldLabel(`meters.${i}.name`, c.entity_type) });
      });
      continue;
    }
    if (isEmpty((c as Record<string, unknown>)[key])) out.push({ key, label: fieldLabel(key, c.entity_type, c.role) });
  }
  return out;
}

/**
 * Membre repris de la base existante (actif sans être passé par le pipeline, donc sans date de signature).
 * Aucun champ ne lui est imposé : e-mail, téléphone, etc. restent facultatifs.
 */
export function isImported(c: Pick<CrmContactDraft, 'stage' | 'contract_signed_at'> | null | undefined): boolean {
  return !!c && c.stage === 'active' && isEmpty(c.contract_signed_at);
}

export function nextStage(stage: Stage): Stage | null {
  const idx = STAGE_INDEX[stage];
  return idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : null;
}

/** Champs manquants pour l'étape suivante (indicateur sur la carte). */
export function missingForNextStage(c: CrmContactDraft & { stage: Stage }): MissingField[] {
  const next = nextStage(c.stage);
  return next ? missingFields(c, next, { excludeDialogFields: true }) : [];
}

/* ── Formats ── */

export const digitsOnly = (v: string) => v.replace(/\D/g, '');
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function eanError(value: string | null | undefined): string | null {
  if (isEmpty(value)) return null;
  const ean = digitsOnly(String(value));
  return ean.length === 18 && ean.startsWith('54') ? null : 'Un code EAN belge compte 18 chiffres et commence par 54';
}

/** Erreurs de format par champ. Un champ vide n'est pas une erreur de format. */
export function formatErrors(c: CrmContactDraft): Record<FieldKey, string> {
  const e: Record<FieldKey, string> = {};
  if (!isEmpty(c.email) && !EMAIL_RE.test(String(c.email).trim())) e.email = 'Adresse e-mail invalide';
  if (!isEmpty(c.billing_email) && !EMAIL_RE.test(String(c.billing_email).trim())) e.billing_email = 'Adresse e-mail invalide';
  // Fiche CRM : longueur seulement (les fiches importées ne sont pas bloquées) ; le formulaire contrôle aussi la clé.
  if (!isEmpty(c.national_register) && digitsOnly(String(c.national_register)).length !== 11) {
    e.national_register = 'Le n° de registre national compte 11 chiffres';
  }
  if (!isEmpty(c.company_number) && digitsOnly(String(c.company_number)).length !== 10) {
    e.company_number = 'Le n° BCE compte 10 chiffres (ex. 0123.456.789)';
  }
  if (!isEmpty(c.iban)) {
    const err = ibanError(String(c.iban));
    if (err) e.iban = err;
  }
  if (!isEmpty(c.bic) && !isValidBic(String(c.bic))) e.bic = 'BIC invalide (8 ou 11 caractères, ex. GEBABEBB)';
  // Même règle que la contrainte crm_contacts_direct_debit_check.
  if (c.billing_channel === 'direct_debit' && !(c.role === 'consumer' && c.entity_type === 'physique' && c.mandate_status === 'active')) {
    e.billing_channel = 'Domiciliation réservée à un particulier consommateur avec un mandat SEPA actif';
  }
  (c.meters ?? []).forEach((m, i) => {
    const err = eanError(m.ean_code);
    if (err) e[`meters.${i}.ean_code`] = err;
  });
  const eans = (c.meters ?? []).map(m => digitsOnly(m.ean_code)).filter(Boolean);
  eans.forEach((ean, i) => {
    if (eans.indexOf(ean) !== i) e[`meters.${i}.ean_code`] = 'Ce code EAN est déjà dans la liste';
  });
  for (const k of ['price_cents_kwh', 'sell_price_cents_kwh', 'sharing_price_cents_kwh', 'sharing_sell_price_cents_kwh'] as const) {
    const v = c[k];
    if (v !== null && v !== undefined && !(Number(v) > 0 && Number(v) < 100)) e[k] = 'Prix attendu entre 0 et 100 c€/kWh';
  }
  if (c.annual_fee !== null && c.annual_fee !== undefined && Number(c.annual_fee) < 0) e.annual_fee = 'La cotisation ne peut pas être négative';
  if (c.annual_consumption_kwh !== null && c.annual_consumption_kwh !== undefined && !(Number(c.annual_consumption_kwh) > 0)) {
    e.annual_consumption_kwh = 'Consommation attendue en kWh, supérieure à 0';
  }
  if (c.annual_cost_eur !== null && c.annual_cost_eur !== undefined && Number(c.annual_cost_eur) < 0) e.annual_cost_eur = 'Montant positif attendu';
  if (c.installed_power_kwc !== null && c.installed_power_kwc !== undefined && !(Number(c.installed_power_kwc) > 0)) {
    e.installed_power_kwc = 'Puissance attendue en kWc, supérieure à 0';
  }
  return e;
}

/** Normalise les champs avant enregistrement. */
export function normalizeDraft(c: CrmContactDraft): CrmContactDraft {
  const out: Record<string, unknown> = { ...c };
  for (const k of ['first_name', 'last_name', 'company_name', 'legal_form', 'representative', 'representative_title',
    'email', 'phone', 'domicile_address', 'billing_email', 'billing_address', 'account_holder', 'notes', 'waiting_note']) {
    const v = out[k];
    if (typeof v === 'string') out[k] = v.trim() === '' ? null : v.trim();
  }
  // Société : « Représenté(e) par » du contrat = prénom et nom du représentant(e).
  if (out.entity_type === 'morale' && ('first_name' in c || 'last_name' in c)) {
    const rep = [out.first_name, out.last_name].filter(v => typeof v === 'string' && v).join(' ');
    out.representative = rep || null;
  }
  if (typeof out.email === 'string') out.email = out.email.toLowerCase();
  if (typeof out.billing_email === 'string') out.billing_email = out.billing_email.toLowerCase();
  if (typeof out.national_register === 'string') out.national_register = digitsOnly(out.national_register) || null;
  // Même format que les fiches participants : « BE 0123.456.789 ».
  if (typeof out.company_number === 'string') out.company_number = formatBce(out.company_number);
  if (typeof out.iban === 'string') out.iban = out.iban.replace(/\s/g, '').toUpperCase() || null;
  if (typeof out.bic === 'string') out.bic = normalizeBic(out.bic) || null;
  if (Array.isArray(c.meters)) {
    out.meters = c.meters.map(m => ({
      ...m,
      name: m.name?.trim() || null,
      ean_code: digitsOnly(m.ean_code ?? ''),
      meter_address: m.meter_address?.trim() || null,
    }));
  }
  return out as CrmContactDraft;
}

/* ── Dates ── */

const pad = (n: number) => String(n).padStart(2, '0');

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Premier jour du mois suivant une date (AAAA-MM-JJ). */
export function firstDayOfNextMonth(isoDate: string | null | undefined): string {
  const base = isoDate ? new Date(`${isoDate.slice(0, 10)}T00:00:00`) : new Date();
  const d = new Date(base.getFullYear(), base.getMonth() + 1, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
